import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Trash2, LogOut, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import UserAvatar from '@/components/ui/UserAvatar.tsx';
import AvatarCropperModal from '@/components/profile/AvatarCropperModal.tsx';
import { useAuth } from '@/context/AuthContext.tsx';
import { useAlert } from '@/context/AlertContext.tsx';
import authService from '@/services/authService.ts';

export default function ProfileMenu() {
  const { t } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const { showAlert } = useAlert();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  if (!user) return null;
  const fullName = `${user.firstName} ${user.lastName}`.trim();

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (f) setPendingFile(f);
  };

  const handleSave = async (blob: Blob) => {
    try {
      await authService.uploadAvatar(blob);
      await refreshUser();
      setPendingFile(null);
      showAlert(t('avatar.updated', 'Foto del profilo aggiornata.'), 'success');
    } catch {
      showAlert(t('avatar.error', 'Impossibile aggiornare la foto.'), 'error');
    }
  };

  const handleRemove = async () => {
    try {
      await authService.deleteAvatar();
      await refreshUser();
      showAlert(t('avatar.removed', 'Foto del profilo rimossa.'), 'success');
    } catch {
      showAlert(t('avatar.error', 'Impossibile aggiornare la foto.'), 'error');
    }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPick} />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <UserAvatar name={fullName} src={user.avatarUrl} size={30} />
            <span className="hidden sm:inline">{t('mvpHome.greeting', { name: user.firstName })}</span>
            <ChevronDown className="size-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="flex items-center gap-3 py-2.5">
            <UserAvatar name={fullName} src={user.avatarUrl} size={40} />
            <div className="min-w-0">
              <p className="truncate font-semibold text-foreground">{fullName}</p>
              <p className="truncate text-xs font-normal text-muted-foreground">{user.email}</p>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); fileRef.current?.click(); }}>
            <Camera className="size-4" />
            {user.avatarUrl ? t('avatar.change', 'Cambia foto') : t('avatar.add', 'Carica foto')}
          </DropdownMenuItem>

          {user.avatarUrl && (
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleRemove(); }}>
              <Trash2 className="size-4" />
              {t('avatar.remove', 'Rimuovi foto')}
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem variant="destructive" onSelect={() => logout()}>
            <LogOut className="size-4" />
            {t('nav.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AvatarCropperModal
        file={pendingFile}
        onCancel={() => setPendingFile(null)}
        onSave={handleSave}
      />
    </>
  );
}
